import React, { useRef, useContext, useCallback, useMemo, memo } from 'react';
import { useIntl } from 'react-intl';
import styled, { keyframes, css } from 'styled-components';
import { durations, curves } from '@cbhq/cds-common/motion/tokens';
import { IconButton } from '@cbhq/cds-web/buttons';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { Box } from '@cbhq/cds-web/layout/Box';
import { spacing, palette } from '@cbhq/cds-web/tokens';
import { Link } from '@cbhq/cds-web/typography/Link';
import { ActionType, ComponentType } from '@cbhq/client-analytics';
import { DEFAULT_LOGGED_OUT_CONFIG } from '@cbhq/intl';
import globalNavLogEvent from '../../../utils/globalNavLogEvent.js';
import { useIsHydrated } from '../../../utils/useIsHydrated.js';
import useMainCTACopy from '../../../utils/useMainCTACopy/useMainCTACopy.js';
import useScrollBreakpoint from '../../../utils/useScrollBreakpoint.js';
import useSecondaryCTACopy from '../../../utils/useSecondaryCTACopy/useSecondaryCTACopy.js';
import '../../Link/index.js';
import { AppWidePersistentBanner } from '../AppWidePersistentBanner.js';
import Banner, { BANNER_HEIGHT } from '../Banner.js';
import '../constants/index.js';
import GlobalNavContext from '../context/GlobalNavContext.js';
import '../CountryBanner/index.js';
import LogoWrapper from '../LogoWrapper.js';
import messages from '../messages.js';
import { BoldLabel } from '../NavTypography.js';
import SubNav, { SUBNAV_HEIGHT, LinksContent, ProductNameText, SubNavLink } from '../SubNav.js';
import NavLinkItem from './NavLinkItem.js';
import SignUpButtonUboExperiment from './SignUpButtonUboExperiment.js';
import './Trapdoor/TrapdoorContent/index.js';
import LanguageSelectorContent from './Trapdoor/TrapdoorContent/LanguageSelector/LanguageSelector.js';
import LanguageSelectorTrapdoorContentWrapper from './Trapdoor/TrapdoorContent/LanguageSelectorTrapdoorContentWrapper.js';
import TrapdoorContentWrapper from './Trapdoor/TrapdoorContent/TrapdoorContentWrapper.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { TRAPDOOR_LINKS_MAP_UK, TRAPDOOR_LINKS_MAP, TRAPDOOR_CONTENT_MAP_UK, TRAPDOOR_CONTENT_MAP } from './Trapdoor/TrapdoorContent/TrapdoorContent.js';
import { FLOATING_HEADER_PX_BREAKPOINT, TRAPDOOR_ANIMATION_MS } from '../constants/constants.js';
import Link$1 from '../../Link/Link.js';
import CountryBanner from '../CountryBanner/CountryBanner.js';
const MAIN_NAV_HEIGHT = 66;
const DISCLAIMER_BANNER_HEIGHT = 82;
const SHOW_ANIMATION = keyframes`
  from {
    transform: translateY(${MAIN_NAV_HEIGHT * -1}px);
  }
  to {
    transform: translateY(0);
  }
`;
function MainNav({
  navLinks,
  activeTab,
  setSelectedTab,
  subNavLinks,
  mainCTA,
  secondaryCTA,
  banner,
  maxWidth,
  isInUboExperimentTreatment,
  isInRewardsHubExperiment,
  customLogoUrl,
  localeInformation,
  userLocaleInfo,
  isCountryBannerDismissed,
  disableStickyness
}) {
  const _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  const isHydrated = useIsHydrated();
  const navLinksRef = useRef(Object.fromEntries(navLinks.map(item => [item.key, null])));
  const _useContext = useContext(GlobalNavContext),
    signInUrl = _useContext.signInUrl,
    signUpUrl = _useContext.signUpUrl,
    onClickSignIn = _useContext.onClickSignIn,
    onClickSignUp = _useContext.onClickSignUp,
    showSignInButton = _useContext.showSignInButton,
    showSignUpButton = _useContext.showSignUpButton,
    isUKUser = _useContext.isUKUser,
    isCountrySwitchBannerEnabled = _useContext.isCountrySwitchBannerEnabled,
    detachBannerFromNav = _useContext.detachBannerFromNav;
  const trapdoorLinksMap = isUKUser ? TRAPDOOR_LINKS_MAP_UK : TRAPDOOR_LINKS_MAP;
  const MAIN_TRAPDOOR_CONTENT_MAP = isUKUser ? TRAPDOOR_CONTENT_MAP_UK : TRAPDOOR_CONTENT_MAP;
  const mainCTACopy = useMainCTACopy(mainCTA);
  const secondaryCTACopy = useSecondaryCTACopy(secondaryCTA);
  const detachedBannerRef = useRef(null);
  const detachedBannerHeight = detachedBannerRef.current?.children[0].clientHeight || 0;
  const trapdoorKeys = Object.keys(trapdoorLinksMap);
  const trapdoorLinksRef = useRef(Object.fromEntries(trapdoorKeys.map(key => [key, trapdoorLinksMap[key].map(() => null)])));
  const trapdoorContentRef = useRef(Object.fromEntries(trapdoorKeys.map(key => [key, null])));
  const detached = useScrollBreakpoint({
    top: FLOATING_HEADER_PX_BREAKPOINT
  });

  // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
  const handleCloseTrapdoor = () => {
    setSelectedTab(null);
  };

  // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
  const handleKeydown = event => {
    event.persist();
    if (event.key === 'Escape') {
      setSelectedTab(currentActiveTab => {
        // if the user hits the Esc key while the trapdoor is open,
        if (currentActiveTab) {
          // ...refocus the parent link of the trapdoor
          navLinksRef.current[currentActiveTab]?.focus();
          // ...close the trapdoor
          return null;
        }
        return currentActiveTab;
      });
    }
    // checks if the user has tabbed outside of the trapdoor content
    requestAnimationFrame(() => {
      if (!activeTab) {
        return;
      }
      const currentTrapdoorLink = trapdoorLinksRef.current[activeTab];
      const currentTrapdoorContent = trapdoorContentRef.current[activeTab];
      const activeElement = document?.activeElement instanceof HTMLElement ? document.activeElement : null;

      // Checking both for the existence of link/content refs and the active element
      // not being inside them separately to avoid false positives in this assertion.
      if (currentTrapdoorLink && currentTrapdoorContent && !currentTrapdoorLink.includes(activeElement) && !currentTrapdoorContent.contains(activeElement)) {
        setSelectedTab(null);
      }
    });
  };
  const signInLogClick = useCallback(onClickSignIn => {
    return () => {
      globalNavLogEvent('global_nav_link', {
        action: ActionType.click,
        componentType: ComponentType.link,
        key: signInUrl
      });
      onClickSignIn?.();
    };
  }, [signInUrl]);
  const openLanguageSelector = useCallback(() => {
    globalNavLogEvent('logged_out_nav_desktop_language_selector_open', {
      action: ActionType.hover,
      componentType: ComponentType.button
    });
    setSelectedTab('languageSelector');
  }, [setSelectedTab]);
  const ssrSafeIsDetached = isHydrated && detached;
  const isSupportedCountryFromIp = (userLocaleInfo?.countryFromIp?.toLowerCase() || '') in DEFAULT_LOGGED_OUT_CONFIG;
  const showCountryBanner = isCountrySwitchBannerEnabled && userLocaleInfo && isSupportedCountryFromIp && userLocaleInfo.countryFromIp !== userLocaleInfo.countryFromUrl && !isCountryBannerDismissed;
  const handleOnMouseLeave = useCallback(() => setSelectedTab(null), [setSelectedTab]);
  const languageSelectorTrapdoor = useMemo(() => {
    return /*#__PURE__*/jsxs(Box, {
      height: "100%",
      alignItems: "center",
      children: [/*#__PURE__*/jsx(IconButton, {
        name: "globe",
        variant: "secondary",
        accessibilityLabel: "Close trade modal",
        compact: true,
        onMouseEnter: openLanguageSelector
      }), /*#__PURE__*/jsx(LanguageSelectorTrapdoorContentWrapper, {
        $isActive: activeTab === 'languageSelector',
        children: /*#__PURE__*/jsx(LanguageSelectorContent, {
          userLocaleInfo: userLocaleInfo,
          localeInformation: localeInformation,
          onMouseLeave: handleOnMouseLeave
        })
      })]
    }, "languageSelector");
  }, [activeTab, userLocaleInfo, localeInformation, openLanguageSelector, handleOnMouseLeave]);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(DetachedPlaceholder, {
      $detached: ssrSafeIsDetached
    }), banner && detachBannerFromNav && !isUKUser && /*#__PURE__*/jsx(Box, {
      ref: detachedBannerRef,
      children: /*#__PURE__*/jsx(Banner, {
        message: banner.message,
        url: banner.url,
        onClick: banner.onClick,
        superMessage: banner.superMessage,
        theme: banner.theme,
        textInBlue: banner.textInBlue
      })
    }, "mainNavDetachedBanner"), /*#__PURE__*/jsx(Box, {
      flexDirection: "column",
      width: "100%",
      style: {
        marginTop: detachBannerFromNav ? detachedBannerHeight : 0
      },
      children: /*#__PURE__*/jsxs(OuterWrapper, {
        $detached: ssrSafeIsDetached,
        $hasSubNav: !!subNavLinks,
        $hasBanner: !!(banner && !isUKUser),
        $hasUKDisclaimer: !!isUKUser,
        $disableStickyness: !!disableStickyness,
        children: [/*#__PURE__*/jsxs(Box, {
          tabIndex: -1,
          children: [/*#__PURE__*/jsx(SkipNav, {
            to: "#main",
            color: "primary",
            children: formatMessage(messages.skipToContent)
          }), /*#__PURE__*/jsx(SkipNav, {
            to: "#footer-site-index",
            color: "primary",
            children: formatMessage(messages.skipToTableOfContents)
          })]
        }), /*#__PURE__*/jsx(Wrapper, {
          onKeyDown: handleKeydown,
          onMouseLeave: handleOnMouseLeave,
          children: /*#__PURE__*/jsxs(InnerWrapper, {
            children: [banner && !detachBannerFromNav && !isUKUser && /*#__PURE__*/jsx(Banner, {
              message: banner.message,
              url: banner.url,
              onClick: banner.onClick,
              superMessage: banner.superMessage,
              theme: banner.theme,
              textInBlue: banner.textInBlue
            }), /*#__PURE__*/jsxs(MainNavWrapper, {
              maxWidth: maxWidth,
              children: [/*#__PURE__*/jsx(LogoWrapper, {
                onMouseEnter: handleCloseTrapdoor,
                customLogoUrl: customLogoUrl
              }), /*#__PURE__*/jsx(CenterWrapper, {
                children: navLinks.map(item => {
                  const itemLabel = formatMessage(messages[item.intlLabelKey]);
                  const TrapdoorContent = MAIN_TRAPDOOR_CONTENT_MAP[item.key];
                  const trapdoorContentExists = !!TrapdoorContent;
                  const itemLinkRefs = trapdoorLinksRef.current[item.key];
                  return /*#__PURE__*/jsxs(React.Fragment, {
                    children: [/*#__PURE__*/jsx(NavLinkItem, {
                      // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                      ref: el => navLinksRef.current[item.key] = el,
                      navLink: item,
                      testID: `main-nav-link-${item.key}`,
                      title: formatMessage(messages[item.intlLinkTitleKey ? item.intlLinkTitleKey : item.intlLabelKey]),
                      activeTab: activeTab
                      // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                      ,

                      onMouseEnter: () => {
                        globalNavLogEvent('global_nav_nav_link', {
                          action: ActionType.hover,
                          componentType: ComponentType.link,
                          key: item.key
                        });
                        setSelectedTab(item.key);
                      }
                      // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                      ,

                      onKeyDown: event => {
                        if (trapdoorContentExists && (event.key === 'Enter' || event.key === ' ')) {
                          setSelectedTab(item.key);
                          event.preventDefault();
                          // focus the first link once trapdoor animation finishes
                          setTimeout(() => {
                            itemLinkRefs[0]?.focus();
                          }, TRAPDOOR_ANIMATION_MS);
                        }
                      }
                      // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                      ,

                      onClick: () => {
                        globalNavLogEvent('global_nav_nav_link', {
                          action: ActionType.click,
                          componentType: ComponentType.link,
                          key: item.key
                        });
                      },
                      isButton: trapdoorContentExists,
                      children: /*#__PURE__*/jsx(NavLinkItemText, {
                        "data-synthetic-id": item.dataSyntheticId,
                        children: itemLabel
                      })
                    }, item.key), trapdoorContentExists && /*#__PURE__*/jsx(TrapdoorContentWrapper, {
                      $isActive: activeTab === item.key,
                      children: /*#__PURE__*/jsx(TrapdoorContent, {
                        contentRefs: trapdoorContentRef.current,
                        linkRefs: itemLinkRefs,
                        isInRewardsHubExperiment: isInRewardsHubExperiment
                      })
                    })]
                  }, item.key);
                })
              }), /*#__PURE__*/jsxs(LanguageSelectorButtonWrapper, {
                children: [languageSelectorTrapdoor, showSignInButton && /*#__PURE__*/jsx(Box, {
                  children: /*#__PURE__*/jsx(Link, {
                    accessibilityLabel: formatMessage(messages.signInLink),
                    to: signInUrl,
                    title: formatMessage(messages.signInLink),
                    testID: "header-sign-in-button",
                    onClick: signInLogClick(onClickSignIn),
                    children: /*#__PURE__*/jsx(Button, {
                      analyticsId: "ef4cc5be-7420-4154-a08d-ecaf91e3903b",
                      variant: "secondary",
                      compact: true,
                      tabIndex: -1,
                      children: formatMessage(secondaryCTACopy || messages.signInLink)
                    })
                  })
                }), showSignUpButton && !isInUboExperimentTreatment && /*#__PURE__*/jsx(Link$1, {
                  logEvents: true,
                  to: signUpUrl,
                  title: formatMessage(mainCTACopy || messages.signUpLink),
                  testID: "header-get-started-button",
                  onClick: onClickSignUp,
                  children: /*#__PURE__*/jsx(Button, {
                    compact: true,
                    tabIndex: -1,
                    analyticsId: "88eae06e-df74-4fb2-9173-118a27d2cfa0",
                    children: formatMessage(mainCTACopy || messages.signUpLink)
                  })
                }), showSignUpButton && isInUboExperimentTreatment && /*#__PURE__*/jsx(SignUpButtonUboExperiment, {})]
              })]
            }), isUKUser && /*#__PURE__*/jsx(AppWidePersistentBanner, {}), (!detached || disableStickyness) && showCountryBanner && /*#__PURE__*/jsx(CountryBanner, {
              countryFromIp: userLocaleInfo?.countryFromIp,
              countryFromUrl: userLocaleInfo?.countryFromUrl
            })]
          })
        }), subNavLinks && /*#__PURE__*/jsx(StyledSubNav, {
          links: subNavLinks,
          $detached: ssrSafeIsDetached
        })]
      })
    })]
  });
}
const MainNav$1 = /*#__PURE__*/memo(MainNav);
const DetachedPlaceholder = styled.div`
  height: 0;
  ${({
  $detached
}) => $detached ? css`
          position: relative;
          height: ${MAIN_NAV_HEIGHT}px;
        ` : ''};
`;
const OuterWrapper = styled.header`
  position: static;
  z-index: ${({
  theme
}) => theme.zIndex.top};
  width: 100%;
  height: ${({
  $hasSubNav,
  $hasBanner,
  $hasUKDisclaimer,
  $disableStickyness
}) => $disableStickyness ? '100%' : MAIN_NAV_HEIGHT + ($hasSubNav ? SUBNAV_HEIGHT : 0) + ($hasBanner ? BANNER_HEIGHT : 0) + ($hasUKDisclaimer ? DISCLAIMER_BANNER_HEIGHT : 0) + 1 + 'px'};
  ${({
  $detached,
  $disableStickyness
}) => $detached && !$disableStickyness ? css`
          position: fixed;
          top: 0;
          animation: ${SHOW_ANIMATION} ${durations.moderate1}ms
            cubic-bezier(${curves.global.join(',')});
        ` : ''};
`;
const Wrapper = styled.div`
  width: 100%;
  border-bottom: 1px solid ${p => p.theme.color.border};
  top: 0;
  background: ${({
  theme
}) => theme.color.background};
`;
const StyledSubNav = styled(SubNav)`
  ${({
  $detached
}) => $detached ? css`
          ${LinksContent} {
            padding-top: 8px;
            padding-bottom: 8px;
          }
          ${ProductNameText} {
            top: -0.5px;
          }
          ${SubNavLink} {
            top: -0.5px;
          }
        ` : ''};
`;
const InnerWrapper = styled.div`
  position: relative;
`;
const MainNavWrapper = styled.div.withConfig({
  shouldForwardProp: p => !['maxWidth'].includes(p)
})`
  max-width: ${({
  theme,
  maxWidth
}) => maxWidth ? theme.layout[maxWidth] : theme.layout.width}px;
  margin: 0 auto;
  display: flex;
  align-items: stretch;
  padding: 0 20px;
  box-sizing: content-box;
  min-height: 66px;
`;
const CenterWrapper = styled.nav`
  display: flex;
  flex: 6;
  justify-content: center;
`;
const ButtonWrapper = styled.div`
  display: flex;
  padding: 0 ${spacing[2]};
  flex: 2;
  justify-content: flex-end;
  align-items: center;
`;
const LanguageSelectorButtonWrapper = styled.div`
  display: flex;
  flex: 2;
  justify-content: flex-end;
  align-items: center;
  gap: ${spacing[1]};
`;
const NavLinkItemText = styled(BoldLabel)`
  text-align: center;
`;

// themes from @cb/shared not available, did not see these values in CDS tokens.
// eslint-disable-next-line @cbhq/theme-no-static-value
const SkipNav = styled(Link)`
  position: absolute;
  left: -9999px;
  z-index: 999;
  width: 100%;
  background-color: ${palette.primaryForeground};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;

  &:focus {
    position: relative;
    left: 0;
    border: 2px ${palette.primaryWash};
  }
`;
export { ButtonWrapper, LanguageSelectorButtonWrapper, MainNav$1 as default };
//# sourceMappingURL=MainNav.js.map
